import React, { Dispatch, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../ui/toast';
import { DIALOG_TYPE } from '../../../../../constants';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import {
  BriefcaseBusinessIcon,
  FilePlusIcons,
  HomeIcons,
  LightningIcons,
  OfficeIcons,
} from '../../../../../icons/icons';
import { Button, Tooltip } from '../../../../../ui';
import AsyncCustomSelect from '../../../../../ui/custom_react_select/async_select';
import { CustomSelect } from '../../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../../ui/input/text_input/text_input';
import { createJobTitle } from '../../../../../utils/api_service/job_title';
import {
  createJobDescription,
  getJobDescription,
  getUniqueIdJobDescription,
} from '../../../../../utils/api_service/JobOpenings';
import useDebouncedJobTitleSearch from '../../../../../utils/helpers/AsyncSelect/JobTitleGet';
import { addJobTitleToRedux } from '../../../../../utils/redux_store/features/job_title_reducer';
import {
  DocumentConfiguration,
  DocumentRequirementStatus,
  IDocumentRequirement,
  JobDescription,
  JobReferenceNumberTypes,
  LocationType, showAllTypes, workType } from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import Collapsible from '../../ui/CollapsibleChildren';
import LoadingSpinner from '../../ui/LoaderSpinner';
import DropdownWithModal, { ModalOption } from '../../ui/SelectModalOption';
import { AIScoreWeightingModal, DEFAULT_DOCUMENTS, DocumentConfigurationModal } from './Components';
import { CreateTemplate, LoadJdTemplate } from './Containers';
import { IJobDescriptionTab } from './Interfaces/IJobDescription';
import { WORK_TYPES } from './Interfaces/IWorkTypes';
import ListFields from './ListFieldComponents';
import ImportJobDescription from './ModalContent';

const JobDescriptionTab = ({
  jobDescription,
  setJobDescription,
  jobTitle,
  setJobTitle,
  jobTitleList,
  completedElement,
  editable,
  setStages,
  setJobTitleLabel,
  setAutoSuitability,
}: IJobDescriptionTab) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(NS.adminPages);
  const JD = ADMIN_PAGES_KEYS.jobOpenings.jobDescription;
  const [showAll, setShowAll] = useState<showAllTypes>('showAll');
  const { loadOptionsDebounced } = useDebouncedJobTitleSearch();
  const [loadingJob, setLoadingJob] = useState<boolean>(() => {
    const loadingJob = ((jobDescription.filename &&
      jobDescription.filename !== '' &&
      !jobDescription.completed) ||
      (jobDescription.transcript &&
        jobDescription.transcript !== '' &&
        !jobDescription.completed)) as boolean;
    return loadingJob;
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);

  const [documentConfigurations, setDocumentConfigurations] = useState<DocumentConfiguration[]>(() => {
    // Initialize from jobDescription.documentRequirements
    if (jobDescription.documentRequirements) {
      return DEFAULT_DOCUMENTS.map(doc => {
        const req = jobDescription.documentRequirements?.find(
          r => r.label === doc.type || (doc.type === 'Resume / CV' && r.key === 'resume'),
        );
        if (req) {
          return {
            type: doc.type,
            requirement: req.isMandatory ? 'Mandatory' : 'Optional',
            allowMultiple: req.allowMultiple,
            description: req.description,
          };
        }
        return {
          type: doc.type,
          requirement: 'Disabled',
          allowMultiple: false,
        };
      });
    }
    return DEFAULT_DOCUMENTS.map(doc => ({
      type: doc.type,
      requirement: (doc as any).mandatoryFixed ? 'Mandatory' : 'Disabled',
      allowMultiple: false,
    }));
  });

  // Sync documentConfigurations when jobDescription.documentRequirements changes
  useEffect(() => {
    if (jobDescription.documentRequirements) {
      const updatedConfigs = DEFAULT_DOCUMENTS.map((doc) => {
        const req = jobDescription.documentRequirements?.find(
          (r) =>
            r.label === doc.type ||
            (doc.type === 'Resume / CV' && r.key === 'resume'),
        );
        if (req) {
          return {
            type: doc.type,
            requirement: req.isMandatory
              ? 'Mandatory'
              : 'Optional' as DocumentRequirementStatus,
            allowMultiple: req.allowMultiple,
            description: req.description,
          };
        }
        return {
          type: doc.type,
          requirement: 'Disabled' as DocumentRequirementStatus,
          allowMultiple: false,
        };
      });

      // Only update if the values are actually different to avoid infinite loops
      // Simple JSON check is sufficient here for these data structures
      if (
        JSON.stringify(updatedConfigs) !==
        JSON.stringify(documentConfigurations)
      ) {
        setDocumentConfigurations(updatedConfigs);
      }
    }
  }, [jobDescription.documentRequirements]);

  const options: ModalOption[] = [
    {
      value: 'importJobDescription',
      label: t(JD.importJobDescription),
      modalContent: (closeModal) => (
        <ImportJobDescription
            closeModal={closeModal}
            setLoading={setLoadingJob}
        />
      ),
      icon: (
        <div className='w-6 h-6 flex items-center justify-center rounded-full bg-blue-100'>
          <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-3 w-3 text-blue-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10'
            />
          </svg>
        </div>
      ),
    },
    {
      value: 'loadJobTemplate',
      label: t(JD.loadJobTemplate),
      modalContent: (closeModal) => (
        <LoadJdTemplate
            closeModal={closeModal}
            jobDescription={jobDescription}
            setJobDescription={setJobDescription}
            setJobTitle={setJobTitle}
            onTemplateSelect={(template) => {
            // Handle template selection - apply template data to current form
              toast.success(`Template "${template.templateTitle}" loaded successfully!`, {
                position: 'bottom-center',
              });
            // You can add logic here to populate the form with template data
            }}
        />
      ),
      disabled: false,
      icon: (
        <div className='w-6 h-6 flex items-center justify-center rounded-full bg-orange-100'>
          <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-3 w-3 text-orange-500'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10'
            />
          </svg>
        </div>
      ),
    },
    {
      value: 'createJobTemplate',
      label: t(JD.createJobTemplate),
      modalContent: (closeModal) => (
        <CreateTemplate
            closeModal={closeModal}
            jobDescription={jobDescription}
            jobTitle={jobTitle}
            onTemplateSave={(templateData) => {
            // Handle template save success
              toast.success('Template saved successfully!', {
                position: 'bottom-center',
              });
            }}
        />
      ),
      disabled: false,
      icon: (
        <div className='w-6 h-6 flex items-center justify-center rounded-full bg-green-100'>
          <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-3 w-3 text-green-600'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 4v16m8-8H4'
            />
          </svg>
        </div>
      ),
    },
  ];

  const defaultJobTitles = jobTitleList.map((jobTitle) => ({
    value: jobTitle.id,
    label: jobTitle.jobTitle,
  }));

  useEffect(() => {
    const fetchUniqueId = async () => {
      try {
        const response = await getUniqueIdJobDescription();
        const data = await response.json();
        setJobDescription((prevState) => ({
          ...prevState,
          jobReferenceNumberAuto: data.id,
        }));
      } catch (error) {
        toast.error('Error Fetching Unique Id', { position: 'bottom-center' });
      }
    };
    if (
      jobDescription.jobReferenceNumberAuto === null ||
      jobDescription.jobReferenceNumberAuto === ''
    ) {
      fetchUniqueId();
    }
  }, [jobDescription.jobReferenceNumberAuto]);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    let pollInterval: NodeJS.Timeout;
    if (loadingJob) {
      pollInterval = setInterval(() => {
        getJobDescription(jobDescription.jobOpeningId)
          .then((response) => {
            return response.json();
          })
          .then((data: JobDescription) => {
            // Keep jobTitle as the full object (either string or JobTitle object)
            // Don't convert to ID only - keep the full data structure
            setJobDescription({
              ...data,
              workType: data.workType ?? 'Permanent',
            });

            if (data.completed || data.status === 'Error') {
              setLoadingJob(false);
              clearInterval(pollInterval);
              if (data.status === 'Error') {
                toast.error('Error Processing Job Description, Retry Again', {
                  position: 'bottom-center',
                });
              } else {
                let jobTitleValue = data.jobTitle;
                if (jobTitleValue) {
                  const jobTitleOption: SelectOptions = {
                    label:
                      typeof jobTitleValue !== 'string'
                        ? jobTitleValue?.jobTitle
                        : jobTitleValue,
                    value:
                      typeof jobTitleValue !== 'string'
                        ? jobTitleValue?.id
                        : jobTitleValue,
                  };
                  setJobTitle(jobTitleOption);
                }
              }
            }
          })
          .catch(() => {
            toast.error('Error Fetching Job Description', {
              position: 'bottom-center',
            });
            setLoadingJob(false);
            clearInterval(pollInterval);
          });
      }, 3000);
    }
    return () => clearInterval(pollInterval);
  }, [loadingJob]);

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  // Set initial location type based on jobLocation
  useEffect(() => {
    if (jobDescription.jobLocation) {
      if (jobDescription.jobLocation === 'Fully Remote') {
        // Only update if not already set to remote
        if (jobDescription.locationType !== 'remote') {
          setJobDescription((prev) => ({
            ...prev,
            locationType: 'remote',
          }));
        }
      } else if (
        !jobDescription.locationType ||
        jobDescription.locationType === 'remote'
      ) {
        // Parse and set location if locationType is not set OR if it's currently remote but jobLocation is not
        const loc = jobDescription.jobLocation.split(',').map((s) => s.trim());
        setJobDescription((prev) => ({
          ...prev,
          locationType: 'onsite',
          city: loc[0] || '',
          state: loc[1] || '',
          country: loc[2] || jobDescription.jobLocation,
        }));
      }
    }
  }, [jobDescription.jobLocation]);

  const defaultWeight = parseFloat((100 / 5).toFixed(2));
  const defaultAiScoreWeights = {
    location: defaultWeight,
    minimumWorkExperience: defaultWeight,
    mandatorySkills: defaultWeight,
    desirableSkills: defaultWeight,
    behavioralAttributes: defaultWeight,
  };
  const handleSave = (
    weights: {
      location: number;
      minimumWorkExperience: number;
      mandatorySkills: number;
      desirableSkills: number;
      behavioralAttributes: number;
    } | null,
  ) => {
    setJobDescription((prev) => ({
      ...prev,
      aiScoreWeights: weights || defaultAiScoreWeights,
    }));
    setModalOpen(false);
  };

  const toggleShowAll = () => {
    // Toggle between showAll and collapseAll
    const newShowAllState = showAll === 'showAll' ? 'collapseAll' : 'showAll';
    setShowAll(newShowAllState);
  };

  const handleChange = (selectedOption: SelectOptions) => {
    const workType: workType = selectedOption.value as workType;
    setJobDescription({ ...jobDescription, workType: workType });
  };

  const handleJobTitleChange = (selectedOptions: SelectOptions | null) => {
    if (selectedOptions === null) {
      setJobTitle(undefined);
      setJobDescription({ ...jobDescription, jobTitle: undefined });
      setJobTitleLabel('');
    } else {
      setJobTitle(selectedOptions);
      // Find the full JobTitle object from the list to store both id and label
      const fullJobTitle = jobTitleList.find(
        (jt) => jt.id === selectedOptions.value,
      );
      setJobDescription({
        ...jobDescription,
        jobTitle: fullJobTitle || selectedOptions.value,
      });
      setJobTitleLabel(selectedOptions.label);
    }
  };

  const handleCreateJobTitle = async (inputValue: string) => {
    try {
      const response = await createJobTitle(inputValue);
      if (response && response.id) {
        const newJobTitle: SelectOptions = {
          value: response.id,
          label: response.jobTitle,
        };
        // Add to Redux store
        dispatch(addJobTitleToRedux(newJobTitle));
        // Set as selected value
        setJobTitle(newJobTitle);
        // Store the full response object, not just the ID
        setJobDescription({ ...jobDescription, jobTitle: response });
        setJobTitleLabel(response.jobTitle);
        toast.success('Job Title created successfully', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('Error creating Job Title', {
        position: 'bottom-center',
      });
    }
  };
  // Handle configure Documents
  const handleConfigureDocuments = () => {
    setIsDocModalOpen(true);
  };

  const handleDocumentRequirementChange = (
    type: string,
    requirement: DocumentRequirementStatus,
  ) => {
    // Resume / CV is always Mandatory
    const isLocked = DEFAULT_DOCUMENTS.find(
      (d) => d.type === type && (d as any).mandatoryFixed,
    );
    if (isLocked) return;

    setDocumentConfigurations((prev) => {
      const existingIndex = prev.findIndex((c) => c.type === type);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], requirement };
        return updated;
      }
      return [...prev, { type, requirement }];
    });
  };

  const handleAllowMultipleChange = (type: string, allowMultiple: boolean) => {
    setDocumentConfigurations((prev) => {
      const existingIndex = prev.findIndex((c) => c.type === type);
      if (existingIndex !== -1) {
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], allowMultiple };
        return updated;
      }
      return [...prev, { type, requirement: 'Optional', allowMultiple }];
    });
  };

  const makeAllMandatory = () => {
    setDocumentConfigurations(prev =>
      prev.map(c => ({ ...c, requirement: 'Mandatory' })),
    );
  };

  const makeAllOptional = () => {
    setDocumentConfigurations(prev =>
      prev.map(doc => ({
        ...doc,
        requirement: DEFAULT_DOCUMENTS.find(d => d.type === doc.type && (d as any).mandatoryFixed)
          ? 'Mandatory'
          : 'Optional',
      })),
    );
  };

  const makeAllDisabled = () => {
    setDocumentConfigurations(prev =>
      prev.map(doc => ({
        ...doc,
        requirement: DEFAULT_DOCUMENTS.find(d => d.type === doc.type && (d as any).mandatoryFixed)
          ? 'Mandatory'
          : 'Disabled',
      })),
    );
  };

  // Sync back to initial state when modal closes
  const handleDocModalClose = () => {
    const updatedRequirements: IDocumentRequirement[] = documentConfigurations
      .filter((config) => config.requirement !== 'Disabled')
      .map((config) => ({
        label: config.type,
        isMandatory: config.requirement === 'Mandatory',
        allowMultiple:
          config.type !== 'Resume / CV' && config.type !== 'Cover Letter'
            ? config.allowMultiple ?? false
            : false,
        acceptedFileTypes: ['.pdf', '.doc', '.docx'],
        description: config.description || '',
        key: config.type === 'Resume / CV' ? 'resume' : config.type,
      }));

    setJobDescription((prev) => ({
      ...prev,
      documentRequirements: updatedRequirements,
    }));
    setIsDocModalOpen(false);
  };
  const validateJobDescription = () => {
    const {
      jobTitle,
      jobReferenceNumber,
      jobLocation,
      workType,
      minimumWorkExperience,
    } = jobDescription;

    if (!jobTitle) {
      toast.error('Job Title is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!jobReferenceNumber) {
      toast.error('Job Reference Number is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!jobLocation) {
      toast.error('Job Location is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (!workType) {
      setJobDescription({ ...jobDescription, workType: 'Permanent' });
      toast.error('Work Type is a required field', {
        position: 'bottom-center',
      });
      return false;
    }

    if (minimumWorkExperience == null) {
      toast.error('Minimum Work Experience is a required field', {
        position: 'bottom-center',
      });
      return false;
    }
    return true;
  };

  const submitJobDescription = async () => {
    const fieldsValid = validateJobDescription();
    if (!fieldsValid) {
      return;
    }
    setLoading(true);
    try {
      // Always send aiScoreWeights, even if never opened
      const payload = {
        ...jobDescription,
        aiScoreWeights: jobDescription.aiScoreWeights || defaultAiScoreWeights,
      };
      const response = await createJobDescription(payload);
      if (response.ok) {
        const data: JobDescription = await response.json();
        setJobDescription({
          ...jobDescription,
          id: data.id,
        });

        // Sync Job Description fields to Auto-Suitability
        const skillsToAutoEvaluation = (skills: string[] | undefined) =>
          (skills || []).map((skill) => ({
            value: skill,
            relationToNext: 'OR' as const,
          }));

        setAutoSuitability((prev) => ({
          ...prev,
          jobLocation: jobDescription.jobLocation || prev.jobLocation,
          skills: skillsToAutoEvaluation(jobDescription.mandatorySkills),
          desirableSkills: skillsToAutoEvaluation(jobDescription.desirableSkills),
          educations: skillsToAutoEvaluation(jobDescription.educations),
          certifications: skillsToAutoEvaluation(jobDescription.certifications),
          behavioralAttributes: skillsToAutoEvaluation(jobDescription.behavioralAttributes),
          workExperience: {
            ...prev.workExperience,
            value: jobDescription.minimumWorkExperience || prev.workExperience.value,
          },
        }));

        setStages((prevStages: any[]) =>
          prevStages.map((stage) =>
            stage.label === 'Job Description'
              ? { ...stage, status: 'Completed' }
              : stage,
          ),
        );
        toast.success('Job Description Saved Successfully', {
          position: 'bottom-center',
        });
        completedElement();
      } else {
        toast.error('Error Submitting Job Description', {
          position: 'bottom-center',
        });
      }
    } catch {
      toast.error('Error Submitting Job Description', {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const changeJobReferenceType = (value: JobReferenceNumberTypes) => {
    if (value === 'Manual') {
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: '',
        jobReferenceType: value,
      });
    } else if (value === 'Auto') {
      console.log('test');
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: jobDescription.jobReferenceNumberAuto || '',
        jobReferenceType: value,
      });
    } else {
      setJobDescription({
        ...jobDescription,
        jobReferenceNumber: jobDescription.jobReferenceNumberJD || '',
        jobReferenceType: value,
      });
    }
  };

  return (
    <>
      { loadingJob ? (
        <LoadingSpinner label={jobDescription.status} />
      ) : (
        <div className='relative flex flex-col gap-4 h-full min-w-0'>
          { !editable && (
            <div className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2'>
              <div className='text-sm font-medium text-amber-950'>
                { t(JD.notEditableBanner) }{ ' ' }
                <span className='text-amber-900'>
                  { t(JD.notEditableDetail) }
                </span>
              </div>
            </div>
          ) }
          <div className='flex flex-col gap-3 mb-1 xl:flex-row xl:items-center xl:justify-between'>
            <div className='flex items-center'>
              <button
                  onClick={() => setShowAll('showAll')}
                  className={`min-h-9 px-3 py-1.5 text-xs font-medium rounded-l-md border transition-colors ${
                    showAll === 'showAll'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                { t(JD.showAll) }
              </button>
              <button
                  onClick={() => setShowAll('collapseAll')}
                  className={`min-h-9 px-3 py-1.5 text-xs font-medium rounded-r-md border transition-colors ${
                    showAll === 'collapseAll'
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
              >
                { t(JD.collapseAll) }
              </button>
            </div>

            <div className='flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end'>
              <Button
                  onClick={handleConfigureDocuments}
                  variant='primary'
                  label={t(JD.configureDocuments)}
                  hasIcon={true}
                  icon={<FilePlusIcons className='w-4 h-4' />}
                  disabled={!editable}
                  className='w-full shadow-sm sm:w-auto'
                  dialogType={DIALOG_TYPE.INFO}

              />
              <div className='relative inline-block'>
                <div className={`${!editable ? 'group' : ''}`}>
                  { ' ' }
                  <Button
                      onClick={handleOpen}
                      variant='primary'
                      label={t(JD.cvScoreWeighting)}
                      hasIcon={true}
                      icon={<LightningIcons className='w-4 h-4' />}
                      disabled={!editable}
                      className='w-full shadow-sm sm:w-auto'
                  />
                  { !editable && jobDescription.aiScoreWeights && (
                    <div className='invisible group-hover:visible absolute z-50 w-80 top-52 left-1/2 -translate-y-full -translate-x-1/2 px-3 py-2 bg-gray-900 rounded-lg text-white text-sm'>
                      <div className='font-medium mb-1'>
                        { t(JD.cvScoreWeightingsTitle) }
                      </div>
                      <div className='space-y-1'>
                        <div>
                          { t(JD.weightingLocation) } { jobDescription.aiScoreWeights.location }%
                        </div>
                        <div>
                          { t(JD.weightingMinWorkExp) }{ ' ' }
                          { jobDescription.aiScoreWeights.minimumWorkExperience }%
                        </div>
                        <div>
                          { t(JD.weightingMandatorySkills) }{ ' ' }
                          { jobDescription.aiScoreWeights.mandatorySkills }%
                        </div>
                        <div>
                          { t(JD.weightingDesirableSkills) }{ ' ' }
                          { jobDescription.aiScoreWeights.desirableSkills }%
                        </div>
                        <div>
                          { t(JD.weightingBehavioural) }{ ' ' }
                          { jobDescription.aiScoreWeights.behavioralAttributes }%
                        </div>
                      </div>
                      <div className='absolute left-1/2 -bottom-2 -translate-x-1/2 border-8 border-transparent border-t-gray-900'></div>
                    </div>
                  ) }
                </div>
              </div>
              <DropdownWithModal
                  options={options}
                  disabled={!editable}
                  label={t(JD.generateJobDescription)}
                  className='w-full min-w-[180px] text-sm sm:w-auto'
              />
            </div>
          </div>
          <div className='space-y-3'>
            <Collapsible
                title={t(JD.detailsSectionTitle)}
                isOpen={showAll === 'showAll' || showAll === 'random'} // Always open in random state
                toggleOpen={() => {
                  if (showAll === 'random') {
                    setShowAll('collapseAll');
                  } else {
                    toggleShowAll();
                  }
                }}
            >
              <div className='space-y-3'>
                <div className='grid grid-cols-1 gap-3 xl:grid-cols-2'>
                  { /* Left Column */ }
                  <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                    <div>
                      <div className='flex items-center gap-1.5 mb-1'>
                        <div className='text-gray-800 text-xs font-medium'>
                          { t(JD.jobTitleLabel) }
                        </div>
                        { jobTitle && (
                          <Tooltip label={jobTitle.label}>
                            <div className='p-0.5 bg-blue-50 rounded text-blue-600 cursor-help hover:bg-blue-100 transition-colors shadow-sm border border-blue-100'>
                              <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  className='h-3 w-3'
                                  fill='none'
                                  viewBox='0 0 24 24'
                                  stroke='currentColor'
                              >
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth={2}
                                    d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                />
                              </svg>
                            </div>
                          </Tooltip>
                        ) }
                      </div>
                      <AsyncCustomSelect
                          value={jobTitle}
                          defaultOptions={defaultJobTitles}
                          onChange={handleJobTitleChange}
                          loadOptions={(inputValue, callback) =>
                            loadOptionsDebounced(inputValue, callback)
                          }
                          isMulti={false}
                          disabled={!editable}
                          creatable={true}
                          onCreateOption={handleCreateJobTitle}
                      />
                    </div>
                    <div>
                      <div className='text-gray-800 mb-1 text-xs font-medium'>
                        { t(JD.jobReferenceNumberLabel) }
                      </div>
                      <div className='flex flex-col gap-2 md:flex-row md:items-start'>
                        <div className='flex-1'>
                          <TextInput
                              name='title'
                              value={jobDescription.jobReferenceNumber || ''}
                              type='text'
                              onChange={(e) =>
                                setJobDescription({
                                  ...jobDescription,
                                  jobReferenceNumber: e.target.value,
                                })
                              }
                              disabled={
                                jobDescription.jobReferenceType === 'Auto' ||
                              jobDescription.jobReferenceType === 'JD' ||
                              !editable
                              }
                              placeholder='Enter reference number'
                          />
                        </div>
                        <div className='flex w-full bg-gray-100 p-0.5 rounded-lg md:w-auto'>
                          { ['Auto', 'Manual', 'JD'].map((type) => (
                            <button
                                key={type}
                                onClick={() =>
                                  changeJobReferenceType(
                                  type as JobReferenceNumberTypes,
                                  )
                                }
                                disabled={!editable}
                                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-all md:flex-none ${
                                  jobDescription.jobReferenceType === type
                                    ? 'bg-white shadow-sm text-blue-600'
                                    : 'text-gray-600 hover:text-gray-900'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              { type }
                            </button>
                          )) }
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className='text-gray-800 mb-1 text-xs font-medium'>
                        { t(JD.workTypeLabel) }
                      </div>
                      <CustomSelect
                          name='jobRequisitionType'
                          value={{
                            label: jobDescription.workType
                              ? jobDescription.workType
                              : 'Permanent',
                            value: jobDescription.workType
                              ? jobDescription.workType
                              : 'Permanent',
                          }}
                          options={WORK_TYPES}
                          onChange={handleChange}
                          disabled={!editable}
                          creatable={true}
                          onCreateOption={handleCreateJobTitle}
                          placeholder='Search or create job title...'
                      />
                    </div>
                    <div>
                      <div className='text-gray-800 mb-1 text-xs font-medium'>
                        { t(JD.minimumWorkExperienceLabel) }
                      </div>
                      <div className='flex items-center gap-1'>
                        <TextInput
                            name='minimumWorkExperience'
                            value={
                              jobDescription.minimumWorkExperience !==
                              undefined &&
                            jobDescription.minimumWorkExperience !== null &&
                            !Number.isNaN(jobDescription.minimumWorkExperience)
                                ? String(jobDescription.minimumWorkExperience)
                                : ''
                            }
                            type='number'
                            onChange={(e) =>
                              setJobDescription({
                                ...jobDescription,
                                minimumWorkExperience:
                                e.target.value === ''
                                  ? undefined
                                  : parseInt(e.target.value),
                              })
                            }
                            disabled={!editable}
                        />
                        <span className='ml-1 text-xs text-gray-500'></span>
                      </div>
                    </div>
                  </div>
                  { /* Right Column - Location */ }
                  <div>
                    <div className='text-gray-800 mb-1 text-xs font-medium'>
                      { t(JD.jobLocationLabel) }
                    </div>
                    <div className='space-y-2.5'>
                      <div className='flex flex-col bg-gray-100 p-0.5 rounded-lg sm:flex-row'>
                        { [
                          {
                            type: 'remote',
                            label: t(JD.fullyRemote),
                            icon: <HomeIcons className='w-4 h-4' />,
                          },
                          {
                            type: 'hybrid',
                            label: t(JD.hybrid),
                            icon: <BriefcaseBusinessIcon className='w-4 h-4' />,
                          },
                          {
                            type: 'onsite',
                            label: t(JD.fullyOnsite),
                            icon: <OfficeIcons className='w-4 h-4' />,
                          },
                        ].map(({ type, label, icon }) => (
                          <button
                              key={type}
                              onClick={() => {
                                const loc =
                                jobDescription.jobLocation
                                  ?.split(',')
                                  .map((s) => s.trim()) || [];
                                setJobDescription({
                                  ...jobDescription,
                                  locationType: type as LocationType,
                                  ...(type === 'remote'
                                    ? {
                                      jobLocation: 'Fully Remote',
                                      city: '',
                                      state: '',
                                      country: '',
                                    }
                                    : {
                                      city: loc[0] || '',
                                      state: loc[1] || '',
                                      country:
                                        loc[2] ||
                                        jobDescription.jobLocation ||
                                        '',
                                    }),
                                });
                              }}
                              disabled={!editable}
                              className={`flex min-h-10 items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                jobDescription.locationType === type
                                  ? 'bg-white shadow-sm text-blue-600'
                                  : 'text-gray-600 hover:text-gray-900'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            { icon }
                            <span className='text-sm font-medium'>{ label }</span>
                          </button>
                        )) }
                      </div>

                      { !jobDescription.locationType &&
                        jobDescription.jobLocation && (
                        <div className='text-sm text-gray-600 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200'>
                          <div className='font-medium mb-1'>
                            { t(JD.currentLocation) }
                          </div>
                          <div className='flex items-center gap-2'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='h-4 w-4 text-gray-400'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                            >
                              <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                              />
                              <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                              />
                            </svg>
                            { jobDescription.jobLocation }
                          </div>
                        </div>
                      ) }

                      { (jobDescription.locationType === 'hybrid' ||
                        jobDescription.locationType === 'onsite') && (
                        <div className='bg-gray-50 p-4 rounded-lg border border-gray-200'>
                          <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                            { [
                              {
                                label: t(JD.city),
                                name: 'city' as const,
                                placeholder: t(JD.city),
                              },
                              {
                                label: t(JD.stateProvince),
                                name: 'state' as const,
                                placeholder: t(JD.stateProvince),
                              },
                              {
                                label: t(JD.country),
                                name: 'country' as const,
                                placeholder: t(JD.country),
                              },
                            ].map((field) => (
                              <div key={field.name}>
                                <label className='block text-xs font-medium text-gray-700 mb-1'>
                                  { field.label }
                                </label>
                                <TextInput
                                    type='text'
                                    name={field.name}
                                    placeholder={field.placeholder}
                                    value={jobDescription[field.name] || ''}
                                    onChange={(e) => {
                                      const updates = {
                                        ...jobDescription,
                                        [field.name]: e.target.value,
                                      };
                                      const location = [
                                        updates.city,
                                        updates.state,
                                        updates.country,
                                      ]
                                        .filter(Boolean)
                                        .join(', ');

                                      setJobDescription({
                                        ...updates,
                                        jobLocation: location || e.target.value,
                                      });
                                    }}
                                    disabled={!editable}
                                />
                              </div>
                            )) }
                          </div>
                          { (jobDescription.city ||
                            jobDescription.state ||
                            jobDescription.country) && (
                            <div className='mt-4 text-sm text-gray-600'>
                              <div className='font-medium mb-1'>
                                { t(JD.locationPreview) }
                              </div>
                              <div className='flex items-center gap-2 py-2'>
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='h-4 w-4 text-gray-400'
                                    fill='none'
                                    viewBox='0 0 24 24'
                                    stroke='currentColor'
                                >
                                  <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                                  />
                                  <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                                  />
                                </svg>
                                { jobDescription.jobLocation }
                              </div>
                            </div>
                          ) }
                        </div>
                      ) }
                    </div>
                  </div>
                </div>
              </div>
            </Collapsible>
          </div>

          <div>
            <ListFields
                showAll={showAll}
                setShowAll={setShowAll}
                jobDescription={jobDescription}
                disabled={!editable}
                setJobDescription={setJobDescription}
                editable={editable}
            />
          </div>
          <AIScoreWeightingModal
              isOpen={isModalOpen}
              onClose={handleClose}
              onSave={handleSave}
              aiScoreWeights={jobDescription.aiScoreWeights}
          />
          <DocumentConfigurationModal
              isOpen={isDocModalOpen}
              onClose={handleDocModalClose}
              editable={editable}
              documentConfigurations={documentConfigurations}
              onDocumentRequirementChange={handleDocumentRequirementChange}
              onAllowMultipleChange={handleAllowMultipleChange}
              onMakeAllMandatory={makeAllMandatory}
              onMakeAllOptional={makeAllOptional}
              onMakeAllDisabled={makeAllDisabled}
          />
          <div className='flex justify-end gap-2 pt-3 mt-3 border-t'>
            <Button
                onClick={() => submitJobDescription()}
                variant='primary'
                label={t(JD.saveJobDescription)}
                className='w-full sm:w-auto'
                disabled={
                  loading ||
                !editable ||
                jobDescription.minimumWorkExperience === undefined ||
                jobDescription.jobTitle === '' ||
                jobDescription.jobLocation === '' ||
                jobDescription.jobReferenceNumber === '' ||
                (jobDescription.workType !== 'Permanent' &&
                  jobDescription.workType !== 'Part Time' &&
                  jobDescription.workType !== 'Casual' &&
                  jobDescription.workType !== 'Contract')
                }
            />
          </div>
        </div>
      ) }
    </>
  );
};

export default JobDescriptionTab;
